import { useApolloClient } from '@vue/apollo-composable';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { LogoutDocument, MeDocument } from '@dc/graphql';

import router from '@/core/router';
import { isGuestId } from '@/shared/guest';

import type { UserFieldsFragment } from '@dc/graphql';

export type CurrentUser = UserFieldsFragment;

export const useAuthStore = defineStore('auth', () => {
  const { client } = useApolloClient();

  const user = ref<CurrentUser | null>();
  const isMeLoading = ref(false);
  const isLoggingOut = ref(false);

  /**
   * `network-only` on purpose: this is the session check, and a cached answer
   * would keep a revoked session alive for the lifetime of the tab.
   */
  const fetchMe = async () => {
    isMeLoading.value = true;

    try {
      const result = await client.query({
        query: MeDocument,
        fetchPolicy: 'network-only',
      });

      if (!result.data?.me) {
        return;
      }

      user.value = result.data.me;
    } finally {
      isMeLoading.value = false;
    }
  };

  /**
   * The API never rejects an unauthenticated request — it installs the guest
   * user and lets the permission guards decide. So "logged out" is a specific
   * id, not a missing user. See `shared/guest.ts` for why this is an exact
   * comparison and not a substring test.
   */
  const isGuest = computed(() => isGuestId(user.value?.id));

  const logout = async () => {
    if (isLoggingOut.value) {
      return;
    }

    isLoggingOut.value = true;

    try {
      await client.mutate({ mutation: LogoutDocument });
    } catch (e) {
      // A failed logout must NOT abort the teardown. The cookie may already be
      // gone (that is one way the mutation fails), and leaving the user inside
      // the shell with a dead session is worse than an extra sign-in. Callers
      // fire this as `void auth.logout()`, so a rethrow here would also become
      // an unhandled rejection rather than anything a user could act on.
      console.error('[stores/auth] Logout mutation failed, signing out locally anyway:', e);
    } finally {
      isLoggingOut.value = false;
    }

    // Separate try blocks, not one: `clearStore` rejects every query that is in
    // flight when it runs ("Store reset while query was in flight"), and shared
    // with `fetchMe` that rejection skipped the refetch. `user` then still held
    // the SIGNED-IN account, and the redirect below walked into the auth
    // guard's "an authenticated user has no business on the login screen" rule,
    // which bounced it straight back — logout looked like it did nothing.
    try {
      await client.clearStore();
    } catch (e) {
      console.error('[stores/auth] Failed to clear the Apollo store on logout:', e);
    }

    try {
      // Refetch rather than null `user`: the API answers with the guest, which
      // is the state the guards are written against.
      await fetchMe();
    } catch (e) {
      console.error('[stores/auth] Failed to refresh the session after logout:', e);
      // The session is gone whether or not we could confirm it. Leaving the
      // old user in place would let the guards treat this tab as signed in.
      user.value = null;
    }

    // The whole point of the action: leave the private route. A navigation
    // failure here is a real one (a guard refused), not the routine "same
    // route" case — the user is never on /login when they press «Выйти».
    const failure = await router.replace({ name: 'Login' });

    if (failure) {
      console.error('[stores/auth] Could not leave the page after logout:', failure);
    }
  };

  // `user` is intentionally NOT cleared here. Setting it to `null` while
  // MainLayout is mounted used to break its template; it dereferences the
  // current user through optional chaining now, but `$reset` still has no
  // reason to invent a logged-out state — `logout` and `fetchMe` own that.
  const $reset = () => {
    isMeLoading.value = false;
    isLoggingOut.value = false;
  };

  return {
    user,
    isMeLoading,
    isLoggingOut,
    isGuest,
    fetchMe,
    logout,
    $reset,
  };
});
